import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatUTCTimeOnlyToLocal } from '../../utils/timezone';
import { getAdminHeaders } from '../../utils';
import './EmergencyContactsPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

interface CheckedInVolunteer {
  id: number;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  emergencyFirstName: string;
  emergencyLastName: string;
  emergencyPhoneNumber: string;
  emergencyRelationship: string;
  passcode: string;
  checkInTime: string;
  isMinor: boolean;
}

const EmergencyContactsPage: React.FC = () => {
  const navigate = useNavigate();
  const [volunteers, setVolunteers] = useState<CheckedInVolunteer[]>([]);
  const [filteredVolunteers, setFilteredVolunteers] = useState<CheckedInVolunteer[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVolunteer, setSelectedVolunteer] = useState<CheckedInVolunteer | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showAllVolunteers, setShowAllVolunteers] = useState(false);

  useEffect(() => {
    fetchVolunteers();
  }, [showAllVolunteers]);

  useEffect(() => {
    filterVolunteers();
  }, [volunteers, searchTerm]);

  const fetchVolunteers = async () => {
    setIsLoading(true);
    try {
      const endpoint = showAllVolunteers 
        ? `${API_BASE_URL}/all-volunteers-emergency`
        : `${API_BASE_URL}/checked-in-volunteers`;
      
      const response = await fetch(endpoint, {
        headers: getAdminHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        // Ensure data is always an array
        setVolunteers(Array.isArray(data) ? data : []);
      } else if (response.status === 401) {
        navigate('/admin-login');
      } else {
        setMessage(`Failed to load ${showAllVolunteers ? 'all' : 'checked-in'} volunteers`);
        setVolunteers([]);
      }
    } catch (error) {
      setMessage('Failed to connect to server');
      setVolunteers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filterVolunteers = () => {
    // Ensure volunteers is always an array
    if (!Array.isArray(volunteers)) {
      setFilteredVolunteers([]);
      return;
    }

    let filtered = volunteers;

    if (searchTerm) {
      filtered = filtered.filter(volunteer =>
        volunteer.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        volunteer.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${volunteer.firstName} ${volunteer.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredVolunteers(filtered);
  };

  const handleVolunteerClick = (volunteer: CheckedInVolunteer) => {
    setSelectedVolunteer(volunteer);
  };

  const handleCloseModal = () => {
    setSelectedVolunteer(null);
  };

  const handleBack = () => {
    navigate('/admin-panel');
  };

  const formatCheckInTime = (checkInTime: string) => {
    return formatUTCTimeOnlyToLocal(checkInTime);
  };

  return (
    <div className="emergency-contacts-container">
      <div className="emergency-contacts-card">
        <div className="emergency-contacts-header">
          <div className="logo">
            <img src="/VolunteerHeart.png" alt="FabMo Volunteer" className="logo-image" />
          </div>
          <h1 className="emergency-contacts-title">Emergency Contacts</h1>
          <p className="emergency-contacts-subtitle">
            Currently checked-in volunteers and their emergency contact information
          </p>
        </div>

        <div className="emergency-contacts-content">
          {/* Search Bar and Toggle */}
          <div className="search-section">
            <div className="search-controls">
              <input
                type="text"
                placeholder="Search by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <div className="toggle-section">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={showAllVolunteers}
                    onChange={(e) => setShowAllVolunteers(e.target.checked)}
                    className="toggle-checkbox"
                  />
                  <span className="toggle-text">Show All Volunteers</span>
                </label>
              </div>
            </div>
            <div className="volunteer-count">
              {filteredVolunteers?.length || 0} volunteer{(filteredVolunteers?.length || 0) !== 1 ? 's' : ''} 
              {showAllVolunteers ? ' (all)' : ' checked in'}
            </div>
          </div>

          {/* Message */}
          {message && (
            <div className={`message ${message.includes('Failed') ? 'error' : 'success'}`}>
              {message}
            </div>
          )}

          {/* Volunteers List */}
          <div className="volunteers-list">
            {isLoading ? (
              <div className="loading">Loading checked-in volunteers...</div>
            ) : filteredVolunteers.length === 0 ? (
              <div className="no-volunteers">
                {searchTerm ? 'No volunteers found matching your search' : 'No volunteers currently checked in'}
              </div>
            ) : (
              <div className="volunteers-table">
                <div className={`table-header ${showAllVolunteers ? 'show-all' : ''}`}>
                  <div className="col-name">Name</div>
                  <div className="col-phone">Phone</div>
                  <div className="col-emergency">Emergency Contact</div>
                  <div className="col-relationship">Relationship</div>
                  <div className="col-emergency-phone">Emergency Phone</div>
                  {showAllVolunteers && <div className="col-status">Status</div>}
                  <div className="col-checkin">Checked In</div>
                </div>
                <div className="table-rows-container">
                  {filteredVolunteers.map((volunteer) => (
                    <div 
                      key={volunteer.id} 
                      className={`table-row ${showAllVolunteers ? 'show-all' : ''}`}
                      onClick={() => handleVolunteerClick(volunteer)}
                    >
                      <div className="col-name">
                        {volunteer.firstName} {volunteer.lastName}
                        {volunteer.isMinor && <span className="minor-badge">Minor</span>}
                      </div>
                      <div className="col-phone">{volunteer.phoneNumber}</div>
                      <div className="col-emergency">
                        {volunteer.emergencyFirstName} {volunteer.emergencyLastName}
                      </div>
                      <div className="col-relationship">{volunteer.emergencyRelationship}</div>
                      <div className="col-emergency-phone">{volunteer.emergencyPhoneNumber}</div>
                      {showAllVolunteers && (
                        <div className="col-status">
                          <span className={`status-badge ${volunteer.checkInTime ? 'checked-in' : 'not-checked-in'}`}>
                            {volunteer.checkInTime ? 'Checked In' : 'Not Checked In'}
                          </span>
                        </div>
                      )}
                      <div className="col-checkin">
                        {volunteer.checkInTime ? (
                          <span className="check-in-time">
                            {formatCheckInTime(volunteer.checkInTime)}
                          </span>
                        ) : (
                          <span className="not-checked-in-text">Not checked in</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="emergency-contacts-footer">
          <button onClick={handleBack} className="back-button">
            ← Back to Admin Panel
          </button>
        </div>
      </div>

      {/* Detailed Contact Modal */}
      {selectedVolunteer && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Emergency Contact Details</h2>
              <button className="close-button" onClick={handleCloseModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="volunteer-info">
                <h3>{selectedVolunteer.firstName} {selectedVolunteer.lastName}</h3>
                {selectedVolunteer.isMinor && <span className="minor-badge">Minor</span>}
                <p><strong>Phone:</strong> {selectedVolunteer.phoneNumber}</p>
                <p><strong>User code:</strong> {selectedVolunteer.passcode}</p>
                <p><strong>Checked In:</strong> {formatCheckInTime(selectedVolunteer.checkInTime)}</p>
              </div>
              
              <div className="emergency-info">
                <h3>Emergency Contact</h3>
                <p><strong>Name:</strong> {selectedVolunteer.emergencyFirstName} {selectedVolunteer.emergencyLastName}</p>
                <p><strong>Relationship:</strong> {selectedVolunteer.emergencyRelationship}</p>
                <p><strong>Phone:</strong> {selectedVolunteer.emergencyPhoneNumber}</p>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={handleCloseModal} className="close-modal-btn">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmergencyContactsPage;
